/* eslint-disable no-nested-ternary */

// biome-ignore lint/style/noRestrictedImports: legacy import, will be migrated
import { Pagination } from '@crehana/ui';
import { useUITheme } from '@crehana/web';
import { clsx } from 'clsx';
// biome-ignore lint/style/noRestrictedImports: legacy import, will be migrated
import { css } from 'styled-components';

import { BorderSize } from './constants';
import TD from './TD';
import TH from './TH';
import type { TableProps } from './types';

function Table<TData>({
  header,
  compact,
  cols,
  data,
  tableName,
  customKey,
  withPagination,
  currentPage,
  onPageChange,
  totalPages,
  actionBottom,
  dataTestid,
  dark,
  tableContainerProps,
  border = BorderSize.Sm,
  compactHeader,
  hidePadding,
  headerTop = '0',
  contentTopList,
}: TableProps<TData>) {
  const { isDark } = useUITheme();
  const cleanedCols = cols.filter(col => !col.hidden);

  return (
    <div
      className={`w-full ${hidePadding || compact ? '' : 'p-24'}`}
      data-testid={dataTestid}
    >
      {header}
      <div
        {...tableContainerProps}
        className={clsx(tableContainerProps?.className, 'w-full')}
      >
        {contentTopList}
        <table className="tw-w-full tw-border-collapse tw-rounded-0.4">
          <thead className="tw-h-4">
            <tr className="tw-h-4">
              {cleanedCols.map(
                (
                  {
                    label,
                    helpLabel,
                    right,
                    keyAccess,
                    customKey: _customKey,
                    className,
                    center,
                  },
                  i,
                ) => {
                  return (
                    <TH
                      key={
                        (_customKey as string | number | undefined) ||
                        (keyAccess as string | number | undefined)
                      }
                      className={clsx(
                        'whitespace-pre-line sticky top-0 z-3 h-40',
                        className,
                      )}
                      css={css`
                        top: ${headerTop};
                      `}
                      right={right}
                      center={center}
                      compact={compact}
                      dark={dark}
                      helpLabel={helpLabel}
                      isFirst={i === 0}
                      isLast={i + 1 === cleanedCols.length}
                      border={border}
                      tableName={tableName}
                      compactHeader={compactHeader}
                    >
                      {label}
                    </TH>
                  );
                },
              )}
            </tr>
          </thead>
          <tbody>
            {data.map((d, i) => (
              <TR
                // TODO: ask the team why we are using the prop `customkey` instead of `keyAccess`
                // @ts-ignore
                key={d[customKey as string]}
                className={clsx(
                  dark || isDark
                    ? 'hover:bg-base-light-dark-mode'
                    : 'hover:bg-gray-lighter',
                )}
              >
                {cleanedCols.map(
                  (
                    {
                      keyAccess,
                      right,
                      transform,
                      isAction,
                      render,
                      center,
                      customKey: _customKey,
                      rowClassName,
                    },
                    j,
                  ) => {
                    if (isAction && !render) {
                      throw new Error(
                        `If the element is an action you must provide a render method. Index: ${i}`,
                      );
                    }
                    if (!isAction && !keyAccess) {
                      throw new Error(
                        `If the element is not an action you must provide a keyAccess. Index: ${i}`,
                      );
                    }

                    // Just an extra validation to make sure to render null in case the transform execution returns a non-nulleable value (like empty string, false bool, etc)
                    const transformJsx =
                      transform && transform(d[keyAccess], i)
                        ? transform(d[keyAccess], i)
                        : null;

                    return (
                      <TD
                        key={_customKey || (keyAccess as string) || j}
                        right={right}
                        center={center}
                        compact={compact}
                        dark={dark}
                        className={
                          rowClassName instanceof Function
                            ? rowClassName(d[keyAccess], i)
                            : rowClassName
                        }
                      >
                        {
                          (isAction && render
                            ? render(d, i)
                            : transformJsx
                              ? transformJsx
                              : d[keyAccess]) as React.ReactNode
                        }
                      </TD>
                    );
                  },
                )}
              </TR>
            ))}
          </tbody>
        </table>
      </div>
      {withPagination || actionBottom ? (
        <div className="flex items-center">
          <div
            className={clsx(
              'flex pt-36',
              withPagination ? 'w-1/2 justify-start' : 'w-full justify-end',
            )}
          >
            {actionBottom && actionBottom}
          </div>
          {withPagination ? (
            <div className="w-1/2 flex justify-end pt-36">
              <Pagination
                displayPosition="top"
                onPageChange={onPageChange}
                currentPage={currentPage}
                totalPages={totalPages}
                isDark={dark}
              />
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

export default Table;

// @ts-ignore TODO: Fix these before ship production. This TR is necessary to avoid a bug in the styled-components with key in react
const TR = ({ children, ...rest }) => {
  return <tr {...rest}>{children}</tr>;
};
